import { formatAmount, formatDate, formatRound } from "../../../utils/common/format";
import AdRectangle from "../../ads/AdRectangle";
import PageMeta from "../../common/PageMeta";
import NoData from "../../common/NoData";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const MIN_LOADING_MS = 500;

const TABLE_HEADER_STYLE = {
  backgroundColor: '#fdfbf7',
  backgroundImage: 'radial-gradient(circle, rgba(227,0,127,0.2) 1px, transparent 1px), radial-gradient(circle, rgba(227,0,127,0.2) 1px, transparent 1px)',
  backgroundPosition: '2px 0, 6px 2px',
  backgroundSize: '8px 4px',
};

// 等級定義
const GRADE_CONFIG = {
  lt6: { grades: [1,2,3,4,5], hasCarryover: true },
  lt7: { grades: [1,2,3,4,5,6], hasCarryover: true },
  mlt: { grades: [1,2,3,4], hasCarryover: true },
  bg5: { grades: [1,2,3,4,5,6,7], hasCarryover: false },
  qcc: { grades: [1,2,3], hasCarryover: false },
};

const NUMBERS_CONFIG = {
  nm3: { types: [
    { key: 'str', label: 'ストレート' },
    { key: 'box', label: 'ボックス' },
    { key: 'set_str', label: 'セット(スト)' },
    { key: 'set_box', label: 'セット(ボク)' },
    { key: 'mini', label: 'ミニ' },
  ]},
  nm4: { types: [
    { key: 'str', label: 'ストレート' },
    { key: 'box', label: 'ボックス' },
    { key: 'set_str', label: 'セット(スト)' },
    { key: 'set_box', label: 'セット(ボク)' },
  ]},
};

function StatsTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[10px] sm:text-xs md:text-sm border border-primary-500 whitespace-nowrap">
        <thead>
          <tr className="text-primary-500 border-b border-primary-500 text-right" style={TABLE_HEADER_STYLE}>
            <th className="px-2 md:px-4 py-2 font-bold text-left">項目</th>
            <th className="px-2 md:px-4 py-2 font-bold">平均</th>
            <th className="px-2 md:px-4 py-2 font-bold">中央値</th>
            <th className="px-2 md:px-4 py-2 font-bold">最大</th>
            <th className="px-2 md:px-4 py-2 font-bold">最小</th>
            <th className="px-2 md:px-4 py-2 font-bold">期待値</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-dashed divide-primary-500">
          {rows.map((row, i) => (
            <tr key={i} className={row.isTotal ? 'bg-primary-500/5' : ''}>
              <td className="px-2 md:px-4 py-2 text-primary-500 font-bold">{row.label}</td>
              <td className="px-2 md:px-4 py-2 text-right text-primary-500">{row.amount_avg}</td>
              <td className="px-2 md:px-4 py-2 text-right text-primary-500">{row.amount_median}</td>
              <td className="px-2 md:px-4 py-2 text-right text-primary-500">{row.amount_max}</td>
              <td className="px-2 md:px-4 py-2 text-right text-primary-500">{row.amount_min}</td>
              <td className="px-2 md:px-4 py-2 text-right text-primary-500">{row.expected ?? '－'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WinnersTable({ rows }) {
  return (
    <div className="overflow-x-auto mt-2">
      <table className="w-full text-[10px] sm:text-xs md:text-sm border border-primary-500 whitespace-nowrap">
        <thead>
          <tr className="text-primary-500 border-b border-primary-500 text-right" style={TABLE_HEADER_STYLE}>
            <th className="px-2 md:px-4 py-2 font-bold text-left">項目</th>
            <th className="px-2 md:px-4 py-2 font-bold">平均</th>
            <th className="px-2 md:px-4 py-2 font-bold">中央値</th>
            <th className="px-2 md:px-4 py-2 font-bold">最大</th>
            <th className="px-2 md:px-4 py-2 font-bold">最小</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-dashed divide-primary-500">
          {rows.map((row, i) => (
            <tr key={i} className={row.isTotal ? 'bg-primary-500/5' : ''}>
              <td className="px-2 md:px-4 py-2 text-primary-500 font-bold">{row.label}</td>
              <td className="px-2 md:px-4 py-2 text-right text-primary-500">{row.winners_avg}</td>
              <td className="px-2 md:px-4 py-2 text-right text-primary-500">{row.winners_median}</td>
              <td className="px-2 md:px-4 py-2 text-right text-primary-500">{row.winners_max}</td>
              <td className="px-2 md:px-4 py-2 text-right text-primary-500">{row.winners_min}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function LotteryNumlotStatsTemplate({ stats, config, loading }) {
  const [showLoading, setShowLoading] = useState(loading);
  const loadingStartedAt = useRef(Date.now());

  useEffect(() => {
    let timer;
    if (loading) {
      loadingStartedAt.current = Date.now();
      setShowLoading(true);
    } else {
      const elapsed = Date.now() - loadingStartedAt.current;
      const remaining = Math.max(0, MIN_LOADING_MS - elapsed);
      timer = setTimeout(() => setShowLoading(false), remaining);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  if (showLoading) {
    return (
      <div className="container max-w-[768px] mx-auto">
        <div className="py-4 space-y-2 animate-pulse">
          <div className="h-4 bg-primary-500/30 rounded w-3/4" />
          <div className="h-4 bg-primary-500/20 rounded w-1/2" />
          <div className="h-4 bg-primary-500/10 rounded w-2/3" />
        </div>
      </div>
    );
  }

  if (!stats) return <NoData />;

  const key = config?.key;
  const gradeConfig = GRADE_CONFIG[key];
  const numbersConfig = NUMBERS_CONFIG[key];
  const isNumbers = config?.type === 'numbers';

  // 払戻テーブル行
  const amountRows = isNumbers
    ? [
        ...(numbersConfig?.types || []).map(t => ({
          label: `［ ${t.label} ］`,
          amount_avg: formatAmount(stats[`${t.key}_amount_avg`]),
          amount_median: formatAmount(stats[`${t.key}_amount_median`]),
          amount_max: formatAmount(stats[`${t.key}_amount_max`]),
          amount_min: formatAmount(stats[`${t.key}_amount_min`]),
          expected: stats[`${t.key}_expected_value`] != null
            ? `${(stats[`${t.key}_expected_value`] * config?.price).toFixed(1)}円` : null,
        })),
        {
          label: '［ 総合 ］',
          amount_avg: formatAmount(stats.total_prize_avg),
          amount_median: formatAmount(stats.total_prize_median),
          amount_max: formatAmount(stats.total_prize_max),
          amount_min: formatAmount(stats.total_prize_min),
          expected: null,
          isTotal: true,
        },
      ]
    : [
        ...(gradeConfig?.grades || []).map(g => ({
          label: `［ ${g}等 ］`,
          amount_avg: formatAmount(stats[`p${g}_amount_avg`]),
          amount_median: formatAmount(stats[`p${g}_amount_median`]),
          amount_max: formatAmount(stats[`p${g}_amount_max`]),
          amount_min: formatAmount(stats[`p${g}_amount_min`]),
          expected: stats[`p${g}_expected_value`] != null
            ? `${(stats[`p${g}_expected_value`] * config?.price).toFixed(1)}円` : null,
        })),
        {
          label: '［ 総合 ］',
          amount_avg: formatAmount(stats.total_prize_avg),
          amount_median: formatAmount(stats.total_prize_median),
          amount_max: formatAmount(stats.total_prize_max),
          amount_min: formatAmount(stats.total_prize_min),
          expected: null,
          isTotal: true,
        },
      ];

  // 当選口数テーブル行
  const winnersRows = isNumbers
    ? [
        ...(numbersConfig?.types || []).map(t => ({
          label: `［ ${t.label} ］`,
          winners_avg: `${(stats[`${t.key}_winners_avg`] ?? 0).toLocaleString()}口`,
          winners_median: `${(stats[`${t.key}_winners_median`] ?? 0).toLocaleString()}口`,
          winners_max: `${(stats[`${t.key}_winners_max`] ?? 0).toLocaleString()}口`,
          winners_min: `${(stats[`${t.key}_winners_min`] ?? 0).toLocaleString()}口`,
        })),
        {
          label: '［ 総合 ］',
          winners_avg: `${(stats.total_winners_avg ?? 0).toLocaleString()}口`,
          winners_median: `${(stats.total_winners_median ?? 0).toLocaleString()}口`,
          winners_max: `${(stats.total_winners_max ?? 0).toLocaleString()}口`,
          winners_min: `${(stats.total_winners_min ?? 0).toLocaleString()}口`,
          isTotal: true,
        },
      ]
    : [
        ...(gradeConfig?.grades || []).map(g => ({
          label: `［ ${g}等 ］`,
          winners_avg: `${(stats[`p${g}_winners_avg`] ?? 0).toLocaleString()}口`,
          winners_median: `${(stats[`p${g}_winners_median`] ?? 0).toLocaleString()}口`,
          winners_max: `${(stats[`p${g}_winners_max`] ?? 0).toLocaleString()}口`,
          winners_min: `${(stats[`p${g}_winners_min`] ?? 0).toLocaleString()}口`,
        })),
        {
          label: '［ 総合 ］',
          winners_avg: `${(stats.total_winners_avg ?? 0).toLocaleString()}口`,
          winners_median: `${(stats.total_winners_median ?? 0).toLocaleString()}口`,
          winners_max: `${(stats.total_winners_max ?? 0).toLocaleString()}口`,
          winners_min: `${(stats.total_winners_min ?? 0).toLocaleString()}口`,
          isTotal: true,
        },
      ];

  return (
    <>
      <PageMeta
        title={`統計データ | ${config?.label}`}
        description={`${config?.label}の配当金・当選口数の統計データ。平均・中央値・最大・最小・期待値を掲載。`}
        path={`${config?.path}stats`}
        breadcrumbs={[
          { name: "TOP", path: "/" },
          { name: config?.label, path: config?.path },
          { name: "統計データ", path: `${config?.path}stats` },
        ]}
      />

      <div className="container mx-auto p-4 md:p-8 bg-base-50 rounded-xl">
        {/* 見出し */}
        <div className="flex justify-between items-center flex-wrap mb-2 border-b border-b-2 border-primary-500 pb-2">
          <h1 className="text-2xl font-bold text-primary-500">
            {config?.label} 統計データ
          </h1>
          <span className="text-primary-500 font-bold ml-auto">
            {formatDate(stats.refreshed_at)}
          </span>
        </div>

        {/* 集計期間 */}
        <p className="text-[10px] text-primary-500/50 mb-16">
          {formatRound(stats.min_round)}（{formatDate(stats.min_date)}）から
          {formatRound(stats.max_round)}（{formatDate(stats.max_date)}）まで
          合計{(stats.valid_count ?? stats.total_count).toLocaleString()}件で集計
        </p>

        {/* 払戻金統計 */}
        <section className="mb-8">
          <h2 className="text-lg font-bold text-primary-500 mb-4 border-b border-primary-500 pb-1">払戻金統計</h2>
          <StatsTable rows={amountRows} />
          <p className="mt-2 text-[10px] text-primary-500/50 text-right">
            ※期待値は1枚（{formatAmount(config?.price)}）あたりの理論値です
          </p>
        </section>

        {/* 当選口数統計 */}
        <section className="mb-16">
          <h2 className="text-lg font-bold text-primary-500 mb-4 border-b border-primary-500 pb-1">当選口数統計</h2>
          <WinnersTable rows={winnersRows} />
        </section>

        {/* 販売実績 */}
        <section className="mb-16">
          <h2 className="text-lg font-bold text-primary-500 mb-4 border-b border-primary-500 pb-1">販売実績</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-[10px] sm:text-xs md:text-sm border border-primary-500 whitespace-nowrap">
              <thead>
                <tr className="text-primary-500 border-b border-primary-500 text-right" style={TABLE_HEADER_STYLE}>
                  <th className="px-2 md:px-4 py-2 font-bold text-left">項目</th>
                  <th className="px-2 md:px-4 py-2 font-bold">平均</th>
                  <th className="px-2 md:px-4 py-2 font-bold">中央値</th>
                  <th className="px-2 md:px-4 py-2 font-bold">最大</th>
                  <th className="px-2 md:px-4 py-2 font-bold">最小</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dashed divide-primary-500">
                <tr>
                  <td className="px-2 md:px-4 py-2 text-primary-500 font-bold">販売実績</td>
                  <td className="px-2 md:px-4 py-2 text-right text-primary-500">{formatAmount(stats.sales_avg)}</td>
                  <td className="px-2 md:px-4 py-2 text-right text-primary-500">{formatAmount(stats.sales_median)}</td>
                  <td className="px-2 md:px-4 py-2 text-right text-primary-500">
                    <Link to={`${config?.path}detail/${stats.sales_max_round}`} className="underline hover:no-underline">
                      {formatAmount(stats.sales_max)}
                    </Link>
                  </td>
                  <td className="px-2 md:px-4 py-2 text-right text-primary-500">{formatAmount(stats.sales_min)}</td>
                </tr>
                {gradeConfig?.hasCarryover && (
                  <tr>
                    <td className="px-2 md:px-4 py-2 text-primary-500 font-bold">キャリーオーバー</td>
                    <td className="px-2 md:px-4 py-2 text-right text-primary-500">{formatAmount(stats.carryover_avg)}</td>
                    <td className="px-2 md:px-4 py-2 text-right text-primary-500">－</td>
                    <td className="px-2 md:px-4 py-2 text-right text-primary-500">{formatAmount(stats.carryover_max)}</td>
                    <td className="px-2 md:px-4 py-2 text-right text-primary-500">－</td>
                  </tr>
                )}
              </tbody>
            </table>
            {gradeConfig?.hasCarryover && (
              <p className="mt-2 text-[10px] text-primary-500/50 text-right">
                キャリーオーバー発生率：{stats.carryover_rate}%
              </p>
            )}
          </div>
        </section>

        {/* 歴代記録 */}
        <section className="mb-0">
          <h2 className="text-lg font-bold text-primary-500 mb-4 border-b border-primary-500 pb-1">歴代記録</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 border border-primary-500 rounded-lg">
              <p className="text-xs text-primary-500/70 mb-1">
                {isNumbers ? 'ストレート' : '1等'} 歴代最高配当
              </p>
              <p className="text-xl font-bold text-primary-500">
                {formatAmount(isNumbers ? stats.str_amount_max : stats.p1_amount_max)}
              </p>
              {/* 歴代最高配当のLink */}
              <Link
                to={`${config?.path}detail/${isNumbers ? stats.str_amount_max_round : stats.p1_amount_max_round}`}
                className="text-xs text-primary-500 underline hover:no-underline"
              >
                {formatRound(isNumbers ? stats.str_amount_max_round : stats.p1_amount_max_round)}
                （{formatDate(isNumbers ? stats.str_amount_max_date : stats.p1_amount_max_date)}）
              </Link>
            </div>
            <div className="p-4 border border-primary-500 rounded-lg">
              <p className="text-xs text-primary-500/70 mb-1">総合 歴代最高払戻</p>
              <p className="text-xl font-bold text-primary-500">
                {formatAmount(stats.total_prize_max)}
              </p>
              <Link
                to={`${config?.path}detail/${stats.total_prize_max_round}`}
                className="text-xs text-primary-500 underline hover:no-underline"
              >
                {formatRound(stats.total_prize_max_round)}
                （{formatDate(stats.total_prize_max_date)}）
              </Link>
            </div>
          </div>
        </section>
      </div>
              <div className="container mx-auto mt-8 mb-8 flex justify-center flex-wrap gap-4">
                <AdRectangle />
                <AdRectangle />
              </div>
    </>
  );
}